import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { CdrService } from "../services/cdr.service";
import * as cdrsActions from './cdr.actions';
import * as cdrsSelectors from './cdr.selectors';
import { catchError, filter, map, of, switchMap } from "rxjs";
import { concatLatestFrom } from "@ngrx/operators";
import { Store } from "@ngrx/store";

export class CdrEffects {
    actions$: Actions = inject(Actions);
    cdrService = inject(CdrService);
    store = inject(Store);

    loadCdrs$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                cdrsActions.loadCdrList,
                cdrsActions.changeCdrPaginationSuccess,
                cdrsActions.changeCdrRequestSuccess,
                cdrsActions.changeCdrFilterSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(cdrsSelectors.selectCdrsCurrentRequest),
                this.store.select(cdrsSelectors.selectCdrsPagination),
                this.store.select(cdrsSelectors.selectCdrsCurrentFilter)
            ]),
            filter(([_action, request, pagination, _filter]) => !!request && !!pagination),
            switchMap(([_action, cdrRequest, pagination, filter]) => {
                return this.cdrService.searchCdrService(cdrRequest!, filter!, pagination!.pageSize!, pagination!.pageIndex!)
                    .pipe(
                        map((res) => {
                            if (res.isSuccess) {
                                return cdrsActions.loadCdrListSuccess({ cdrResponse: res.value! });
                            } else {
                                return cdrsActions.loadCdrListFailure({ error: res.error! });
                            }
                        }),
                        catchError((err) =>
                            of(cdrsActions.loadCdrListFailure({ error: err.message || 'Loading cdrs failed' })))
                    )
            })
        )
    );

    loadCdrFilterParameters = createEffect(() =>
        this.actions$.pipe(
            ofType(
                cdrsActions.loadCdrFilterParameters,
                cdrsActions.changeCdrRequestSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(cdrsSelectors.selectCdrsCurrentRequest)
            ]),
            filter(([_action, request]) => !!request),
            switchMap(([_action, request]) => {
                return this.cdrService.loadCdrFilterParameters(request!)
                    .pipe(
                        map((res) => {
                            if (res.isSuccess) {
                                return cdrsActions.loadCdrFilterParametersSuccess({ cdrFilters: res.value! });
                            } else {
                                return cdrsActions.loadCdrFilterParametersFailure({ error: res.error! });
                            }
                        }),
                        catchError((err) =>
                            of(cdrsActions.loadCdrFilterParametersFailure({ error: err.message || 'Loading cdrs filter failed' })))
                    )
            })
        )
    );

    changeCdrFilter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cdrsActions.changeCdrFilter),
            map((_) => cdrsActions.changeCdrFilterSuccess())
        )
    );

    changeCdrPagination$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cdrsActions.changeCdrPagination),
            map((_) => cdrsActions.changeCdrPaginationSuccess())
        )
    );

    changeCdrRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cdrsActions.changeCdrRequest),
            map((_) => cdrsActions.changeCdrRequestSuccess())
        )
    )
}